import { Fade } from "@progress/kendo-react-animation";
import { DateTimePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { Notification, NotificationGroup } from "@progress/kendo-react-notification";
import _ from "lodash";
import { DateTime } from "luxon";
import * as React from "react";
import { isEmptyString } from "../../../banner/classes/StringHelper";
import { IImageUploadData } from "../../classes/ImageUploadData";
import { htmlToText } from "../../classes/StringHelper";
import { IConfig } from "../../Config";
import DataService from "../../data/DataService";
import { ConfirmDialog } from "../Dialogs";
import Loader from "../Loader";
import Modal from "../Modal";
import ImagePickerWithPreview from "./components/ImagePickerWithPreview";
import ManageCategories from "./components/ManageCategories";
import NewsEditor from "./components/NewsEditor";
import {
    DatePickerMaxYear, FriendlyUrlMaxLength, FriendlyUrlName_Pattern,
    friendlyUrlNameRegExp, MaxAuthorLength, MaxImageCaptionLength,
    SummaryMaxLength, TitleMaxLength, UnsavedConfirmationMessage,
} from "./consts";
import "./edit.css";

interface IProps {
    availableCategories: any[];
    availableFeeds: any[];
    config: IConfig;
    dataService: DataService;
    newsId: string;
    onBack: () => void;
    onReloadNewsPosts: () => void;
    reloadAvailableCategories: () => Promise<void>;
    user: any;
}

interface IState {
    author: string;
    body: string;
    deleted: boolean;
    errors: any[];
    expiry: Date;
    featuredImageCaption: string;
    featuredImageFileName: string;
    featuredImageFile: IImageUploadData;
    featuredImageUploadedPath: string;
    friendlyUrlName: string;
    hasUnsavedChanges: boolean;
    imageHeight: number;
    imageWidth: number;
    isFriendlyUrlLocked: boolean;
    isLoading: boolean;
    isManageCategoriesOpen: boolean;
    isMobileView: boolean;
    isPromptUnsavedOpen: boolean;
    isSidebarOpen: boolean;
    isSummaryLocked: boolean;
    originalMetadata: any;
    pinToTop: boolean;
    scheduledDate: Date;
    selectedCategories: any[];
    selectedFeed: any;
    summary: string;
    tags: string[];
    title: string;
}

export class Edit extends React.Component<IProps, IState> {
    private static readonly targetImageWidth: number = 960;
    private static readonly targetImageHeight: number = 540;

    public constructor(props, context) {
        super(props, context);

        this.state = {
            author: "",
            body: "",
            deleted: false,
            errors: [],
            expiry: new Date(DatePickerMaxYear, 11, 31, 11, 59),
            featuredImageCaption: "",
            featuredImageFile: null,
            featuredImageFileName: "",
            featuredImageUploadedPath: "",
            friendlyUrlName: "",
            hasUnsavedChanges: false,
            imageHeight: 0,
            imageWidth: 0,
            isFriendlyUrlLocked: true,
            isLoading: true,
            isManageCategoriesOpen: false,
            isMobileView: window.innerWidth < 800,
            isPromptUnsavedOpen: false,
            isSidebarOpen: window.innerWidth >= 800,
            isSummaryLocked: true,
            originalMetadata: null,
            pinToTop: false,
            scheduledDate: new Date(),
            selectedCategories: [],
            selectedFeed: null,
            summary: "",
            tags: [],
            title: "",
        };
    }

    public async componentDidMount() {
        if (this.props.config.role === "user" || this.props.user === null) {
            this.props.onBack();
        }

        this.setState({ isLoading: true });

        if (this.isEditingExistingPost()) {
            await this.loadNews();
        } else {
            this.setState({
                author: `${this.props.user.firstName} ${this.props.user.lastName}`,
            });
            // Delay for a little bit before showing, so that
            // it can slide in using the animation.
            await new Promise((r) => setTimeout(r, 50));
        }

        window.addEventListener("beforeunload", this.onUnload);
        window.addEventListener("resize", this.onResize);
        this.setState({ isLoading: false });
    }

    public render() {
        return (
            <Loader isLoading={this.state.isLoading}>
                <div id="news-editing-panel" className={this.state.isLoading ? "" : "show"}>
                    <this.topBar />
                    <div className="main-area">
                        <this.editArea />
                        <this.sideBar />
                    </div>
                    <this.mobileBottom />
                </div>

                <ConfirmDialog
                    isOpen={this.state.isPromptUnsavedOpen}
                    message={UnsavedConfirmationMessage}
                    callBack={(value) => {
                        if (value) {
                            this.setState({ hasUnsavedChanges: false });
                            this.slideOutAndReturn();
                        }
                        this.setState({ isPromptUnsavedOpen: false });
                    }}
                />

                <Modal
                    overlayClassName="nr-manage-categories-modal-overlay"
                    isClosable={false}
                    isOpen={this.state.isManageCategoriesOpen}
                >
                    <ManageCategories
                        config={this.props.config}
                        onClose={async () => {
                            this.setState({ isManageCategoriesOpen: false });
                            await this.props.reloadAvailableCategories();
                        }}
                    />
                </Modal>
            </Loader>
        );
    }

    private topBar = () => {
        return (
            <div className="top-sec">
                <div className="left-sec">
                    <span className="path">
                        <a
                            href="#"
                            title="Back to Newsromm page"
                            aria-label="Back to Newsromm page"
                            className="red"
                            onClick={this.cancelClick}
                        >
                            Posts
                        </a>
                        {" > "}
                        {this.isEditingExistingPost() ? "Edit Post" : "New Post"}
                    </span>
                </div>
                <div className="middle-sec">
                    <div className="header-title">
                        <span className="fa fa-pencil" aria-hidden="true"></span>
                        <span>Write</span>
                    </div>
                </div>
                <div className="right-sec">
                    <button
                        className="admin-btn blue-btn"
                        type="button"
                        aria-label="Publish"
                        onClick={this.publishClick}
                    >
                        <span>Publish</span>
                    </button>
                    <button
                        className="admin-btn no-text-btn close-btn"
                        type="button"
                        aria-label="Close"
                        onClick={this.cancelClick}
                    >
                        <span className="fa fa-times" aria-hidden="true"></span>
                    </button>
                </div>
            </div>
        );
    }

    private editArea = () => {
        let expandButtonClass = "";
        if (!this.state.isMobileView) {
            expandButtonClass = this.state.isSidebarOpen ? "fa-caret-square-o-right" : "fa-caret-square-o-left";
        } else {
            expandButtonClass = this.state.isSidebarOpen ? "fa-caret-square-o-down" : "fa-caret-square-o-up";
        }

        let editingAreaClass = "";
        if (this.state.isSidebarOpen && this.state.isMobileView) {
            editingAreaClass = "setting-expanded";
        } else if (!this.state.isSidebarOpen && !this.state.isMobileView) {
            editingAreaClass = "expand";
        }

        return (
            <div className={"editing-area " + editingAreaClass}>
                <div className="left-sec">
                    <this.errorsList />
                    <ImagePickerWithPreview
                        errorText={this.getErrorMessage("imageCaption")}
                        imageCaption={this.state.featuredImageCaption}
                        imageCaptionMaxLength={MaxImageCaptionLength}
                        imageFileName={this.state.featuredImageFileName}
                        imageFileSizeLimit={this.props.config.imageFileSizeLimit}
                        onSubmitImage={(file: File, x: number, y: number, w: number, h: number) => {
                            this.setState({
                                featuredImageFile: { file, x, y, w, h },
                                featuredImageFileName: file.name,
                                featuredImageUploadedPath: null,
                                hasUnsavedChanges: true,
                                imageHeight: Math.min(h, Edit.targetImageHeight),
                                imageWidth: Math.min(w, Edit.targetImageWidth),
                                isLoading: false,
                            });
                        }}
                        onDeleteImage={() => {
                            this.setState({
                                featuredImageCaption: null,
                                featuredImageFile: null,
                                featuredImageFileName: null,
                                featuredImageUploadedPath: null,
                                hasUnsavedChanges: true,
                            });
                        }}
                        originalImagePath={this.state.featuredImageUploadedPath}
                        setImageCaption={(newCaption: string) => {
                            this.setState({
                                featuredImageCaption: newCaption,
                                hasUnsavedChanges: true,
                            });
                        }}
                        targetHeight={Edit.targetImageHeight}
                        targetWidth={Edit.targetImageWidth}
                    />
                    <div className="post-content">
                        <input
                            aria-label="Post title"
                            autoFocus={true}
                            type="text"
                            id="postTitle"
                            placeholder="Type a title..."
                            value={this.state.title}
                            maxLength={TitleMaxLength}
                            onChange={(e) => {
                                this.setState({ title: e.target.value, hasUnsavedChanges: true }, () => {
                                    this.autoUpdateFriendlyUrl();
                                });
                            }}
                        />
                        <this.fieldErrors field="title" />
                        <NewsEditor
                            config={this.props.config}
                            body={this.state.body}
                            setBody={(value: string) => {
                                this.setState({
                                    body: value,
                                    hasUnsavedChanges: true,
                                }, () => {
                                    this.autoUpdateSummary();
                                });
                            }}
                            onFocus={(e) => {
                                if (htmlToText(this.state.body).trim() === "") {
                                    this.setState({ isSidebarOpen: false });
                                }
                            }}
                        />
                        <this.fieldErrors field="content" />
                    </div>
                </div>
                <div className="right-sec">
                    <button
                        className={"admin-btn expand-btn nobg-btn border-btn no-text-btn " +
                            (!this.state.isSidebarOpen ? "enabled" : "")}
                        type="button"
                        title={this.state.isSidebarOpen ? "Collapse Post Settings" : "Expand Post Settings"}
                        aria-label={this.state.isSidebarOpen ? "Collapse Post Settings" : "Expand Post Settings"}
                        onClick={(e) => {
                            this.setState({ isSidebarOpen: !this.state.isSidebarOpen });
                        }}
                    >
                        <span
                            className={"fa " + (!this.state.isMobileView ? expandButtonClass : "")}
                            aria-hidden="true"
                        ></span>
                        <span
                            className={"fa mobile " + (this.state.isMobileView ? expandButtonClass : "")}
                            aria-hidden="true"
                        ></span>
                        <span>Settings</span>
                    </button>
                </div>
            </div>
        );
    }

    private errorsList = () => {
        if (this.state.errors.length === 0) {
            return null;
        }

        return (
            <div className="validationError CMS_inlineAlerts CMS_error nr-form-errors">
                {this.state.errors.map((error, i) => (
                    <div key={i}>
                        {error[1] || error[0]}
                    </div>
                ))}
            </div>
        );
    }

    private fieldErrors = (props: any) => {
        return (
            <>
                {
                    this.state.errors.filter((x) => x[0] === props.field).map((x) => (
                        <div className="field-error" key={x[1]}>
                            {x[1]}
                        </div>
                    ))
                }
            </>
        );
    }

    private getErrorMessage = (field: string) => {
        const matchingErrors = this.state.errors.filter((x) => x[0] === "imageCaption");
        if (matchingErrors.length === 0) {
            return "";
        }

        return matchingErrors[0][1];
    }

    private sideBar = () => {
        let settingsClass = "";
        if (!this.state.isMobileView && !this.state.isSidebarOpen) {
            settingsClass = "close";
        } else if (this.state.isMobileView && this.state.isSidebarOpen) {
            settingsClass = "open";
        }

        return (
            <div className={"post-settings " + settingsClass}>
                <button
                    className="admin-btn no-text-btn close-btn"
                    type="button"
                    aria-label="Close"
                    onClick={(e) => {
                        this.setState({ isSidebarOpen: false });
                    }}
                >
                    <span className="fa fa-times" aria-hidden="true"></span>
                </button>
                <div className="setting-title top-section">Post Settings</div>
                <div className="setting-row">
                    <label htmlFor="friendly_url">Friendly URL</label>
                    <div className="input-with-button">
                        <input
                            type="text"
                            id="friendly_url"
                            name="friendly_url"
                            value={this.state.friendlyUrlName}
                            disabled={this.state.isFriendlyUrlLocked}
                            maxLength={FriendlyUrlMaxLength}
                            pattern={FriendlyUrlName_Pattern}
                            onChange={(e) => {
                                this.setState({
                                    friendlyUrlName: e.target.value.toLowerCase().replace(/[^a-z0-9_\-]+/, ""),
                                    hasUnsavedChanges: true,
                                });
                            }}
                        />
                        <button
                            className="admin-btn nobg-btn border-btn lock-btn no-text-btn"
                            type="button"
                            aria-label="Unlock Friendly URL"
                            onClick={(e) => {
                                this.setState({
                                    isFriendlyUrlLocked: !this.state.isFriendlyUrlLocked,
                                }, () => {
                                    this.autoUpdateFriendlyUrl();
                                });
                            }}
                        >
                            <span
                                className={"fa " + (this.state.isFriendlyUrlLocked ? "fa-lock" : "fa-unlock")}
                                aria-hidden="true"
                            ></span>
                        </button>
                    </div>
                    <this.fieldErrors field="friendlyUrl" />
                </div>
                <div className="setting-row">
                    <label htmlFor="summary">Summary</label>
                    <div className="input-with-button">
                        <textarea
                            id="summary"
                            name="summary"
                            rows={4}
                            cols={50}
                            disabled={this.state.isSummaryLocked}
                            value={this.state.summary}
                            maxLength={SummaryMaxLength}
                            onChange={(e) => {
                                this.setState({
                                    hasUnsavedChanges: true,
                                    summary: e.target.value,
                                });
                            }}
                        />
                        <button
                            className="admin-btn nobg-btn border-btn lock-btn no-text-btn"
                            type="button"
                            aria-label="Unlock Summary"
                            onClick={(e) => {
                                this.setState({
                                    isSummaryLocked: !this.state.isSummaryLocked,
                                }, () => {
                                    this.autoUpdateSummary();
                                });
                            }}
                        >
                            <span
                                className={"fa " + (this.state.isSummaryLocked ? "fa-lock" : "fa-unlock")}
                                aria-hidden="true"
                            ></span>
                        </button>
                    </div>
                    <this.fieldErrors field="summary" />
                </div>
                <div className="setting-row">
                    <label htmlFor="feed">Feed</label>
                    <DropDownList
                        id="feed"
                        name="feed"
                        data={this.props.availableFeeds}
                        textField="feedName"
                        dataItemKey="feedId"
                        filterable={true}
                        style={{ width: "100%" }}
                        value={this.state.selectedFeed}
                        onChange={(e) => this.setState({ selectedFeed: e.target.value })}
                    />
                </div>
                <this.fieldErrors field="feed" />
                <div className="setting-row">
                    <label htmlFor="categories">Category<span>(Optional)</span></label>
                    <MultiSelect
                        id="categories"
                        name="categories"
                        data={this.props.availableCategories}
                        onChange={(e) => {
                            this.setState({
                                hasUnsavedChanges: true,
                                selectedCategories: e.target.value,
                            });
                        }}
                        placeholder="Select category..."
                        value={this.state.selectedCategories}
                        textField="categoryName"
                        dataItemKey="categoryId"
                        filterable={true}
                    />
                    {(this.props.config.role === "superadmin") ?
                        <a href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                this.setState({ isManageCategoriesOpen: true });
                            }}
                        >
                            Manage Categories
                        </a> : null
                    }
                </div>
                <div className="setting-row">
                    <label htmlFor="tags">Tags<span>(Optional)</span></label>
                    <MultiSelect
                        id="tags"
                        name="tags"
                        placeholder="Select tags..."
                        data={this.state.tags}
                        onChange={(e) => {
                            const tags = e.target.value;
                            const lastItem = tags[tags.length - 1];
                            if (lastItem) {
                                tags.pop();
                                const sameItem = tags.find((tag) => tag === lastItem);
                                if (sameItem === undefined) {
                                    tags.push(lastItem);
                                }
                            }
                            this.setState({ tags, hasUnsavedChanges: true });
                        }}
                        value={this.state.tags}
                        allowCustom={true}
                    />
                </div>
                <div className="setting-row">
                    <label htmlFor="author">Author</label>
                    <input
                        type="text"
                        id="author"
                        name="author"
                        maxLength={MaxAuthorLength}
                        placeholder="Enter author name..."
                        value={this.state.author}
                        onChange={(e) => {
                            this.setState({
                                author: e.target.value,
                                hasUnsavedChanges: true,
                            });
                        }}
                    />
                </div>
                <this.fieldErrors field="author" />
                <div className="setting-row pin-to-top">
                    <label htmlFor="pinToTop">Pin it to the top</label>
                    <div>Allows you to keep a post on top of the front page of your newsroom</div>
                    <input
                        type="checkbox"
                        id="pinToTop"
                        name="pinToTop"
                        aria-label="Pin to top"
                        checked={this.state.pinToTop}
                        onChange={(e) => {
                            this.setState({
                                hasUnsavedChanges: true,
                                pinToTop: !this.state.pinToTop,
                            });
                        }}
                    />
                </div>
                <div className="setting-title">Schedule</div>
                <div className="setting-row">
                    <label htmlFor="release_date">Release at</label>
                    <DateTimePicker
                        id="release_date"
                        name="release_date"
                        width="100%"
                        className="k-datetime-container date-picker"
                        placeholder="Select date and time..."
                        onChange={(e) => {
                            if (!e.target.value) {
                                return;
                            }
                            this.setState({
                                hasUnsavedChanges: true,
                                scheduledDate: e.target.value,
                            });
                        }}
                        value={this.state.scheduledDate}
                        min={this.isEditingExistingPost() ? this.state.scheduledDate : new Date()}
                    />
                </div>
                <this.fieldErrors field="releaseDate" />
                <div className="setting-row">
                    <label htmlFor="expire_date">Expire at</label>
                    <DateTimePicker
                        id="expire_date"
                        name="expire_date"
                        width="100%"
                        className="k-datetime-container date-picker"
                        placeholder="Select date and time..."
                        onChange={(e) => {
                            if (!e.target.value) {
                                return;
                            }
                            this.setState({
                                expiry: e.target.value,
                                hasUnsavedChanges: true,
                            });
                        }}
                        value={this.state.expiry}
                        min={this.state.scheduledDate}
                    />
                </div>
                <this.fieldErrors field="expiry" />
                <div className="mobile-buttons">
                    <button
                        className="admin-btn"
                        type="button"
                        aria-label="Cancel"
                        style={{ visibility: "hidden" }}
                    >
                        <span>Cancel</span>
                    </button>
                    <button
                        className="admin-btn blue-btn"
                        type="button"
                        aria-label="Done"
                        onClick={(e) => {
                            this.setState({ isSidebarOpen: false });
                        }}
                    >
                        <span>Done</span>
                    </button>
                </div>
            </div>
        );
    }

    private isEditingExistingPost = () => {
        return this.props.newsId?.length > 0;
    }

    private mobileBottom = () => {
        return (
            <div className="mobile-bottom">
                <div className="left-sec">
                    <div className="header-title">
                        <span className="fa fa-pencil" aria-hidden="true"></span> { }
                        <span>Write</span>
                    </div>
                </div>
                <div className="right-sec">
                    <button
                        className="admin-btn"
                        type="button"
                        aria-label="Cancel"
                        onClick={this.cancelClick}
                    >
                        <span>Cancel</span>
                    </button>
                    {" "}
                    <button
                        className="admin-btn blue-btn"
                        type="button"
                        aria-label="Publish"
                        onClick={this.publishClick}
                    >
                        <span>Publish</span>
                    </button>
                </div>
            </div>
        );
    }

    private loadNews = async () => {
        const news = (await this.props.dataService.getNews(this.props.newsId)).data;

        let selectedCategories: any[] = [];
        if (news.categories && news.categories.length > 0) {
            selectedCategories = this.props.availableCategories.filter((ac) => {
                return news.categories.some((nc) => ac.categoryId === nc.categoryId);
            });
        }
        const tags = news.tags?.map((t) => t.tag) || [];

        let metadata = null;
        let isFriendlyUrlLocked = true;
        let isSummaryLocked = true;
        try {
            metadata = JSON.parse(news.metadata);
            isFriendlyUrlLocked = !metadata.isCustomFriendlyUrlName || true;
            isSummaryLocked = !metadata.isCustomSummary || true;
        } catch (e) {
            console.warn(e);
        }

        let featuredImageFileName: string = "";
        if (metadata?.imageName) {
            featuredImageFileName = metadata.imageName;
        } else {
            const temp = (news.featuredImage || "").replaceAll("\\", "/").split("/");
            featuredImageFileName = (!_.isEmpty(temp) ? _.last(temp) : news.featuredImage) || "";
            featuredImageFileName = featuredImageFileName.indexOf("_") < 0 ? featuredImageFileName : featuredImageFileName.substr(featuredImageFileName.indexOf("_") + 1);
        }

        let selectedFeed = null;
        if (news.feeds?.length > 0) {
            const availableSelected = this.props.availableFeeds.filter((f) => f.feedId === news.feeds[0].feedId);
            if (availableSelected.length > 0) {
                selectedFeed = availableSelected[0];
            }
        }

        this.setState({
            author: news.author,
            body: news.body,
            deleted: news.deleted,
            expiry: this.convertToJsDate(news.expiry),
            featuredImageFileName,
            featuredImageCaption: news.featuredImageCaption,
            featuredImageUploadedPath: news.featuredImage,
            friendlyUrlName: news.friendlyUrlName,
            isFriendlyUrlLocked,
            isSummaryLocked,
            originalMetadata: metadata,
            pinToTop: news.feeds.some((f) => f.pinToTop === true),
            scheduledDate: this.convertToJsDate(news.scheduledDate),
            selectedCategories,
            selectedFeed,
            summary: news.summary,
            tags,
            title: news.title,
        });
    }

    private autoUpdateFriendlyUrl = () => {
        if (!this.state.isFriendlyUrlLocked) {
            return;
        }

        if (_.isEmpty(this.state.title)) {
            this.setState({ friendlyUrlName: "" });
            return;
        }

        let friendly = _.trimEnd((this.state.title || "")
            .replace(/[^0-9a-z]+/gi, "_")
            .toLowerCase(), "_");
        friendly = `${DateTime.local().toFormat("LL-dd-yyyy")}_${friendly}`;
        friendly = friendly.substring(0, FriendlyUrlMaxLength);
        this.setState({ friendlyUrlName: friendly });
    }

    private autoUpdateSummary = () => {
        if (!this.state.isSummaryLocked) {
            return;
        }

        const summary = htmlToText(this.state.body || "")
            .trim().substring(0, SummaryMaxLength);

        this.setState({ summary });
    }

    private publishClick = () => {
        const errors = this.getValidationErrors();
        this.setState({ errors });
        if (errors.length > 0) {
            return;
        }

        this.publishSave();
    }

    private getValidationErrors = () => {
        const errors = [];
        if (isEmptyString(this.state.title)) {
            errors.push(["title", "Title is required"]);
        }

        const div = document.createElement("div");
        div.innerHTML = this.state.body;
        if (!div.firstChild?.hasChildNodes()) {
            errors.push(["content", "Content is required"]);
        }

        if (!isEmptyString(this.state.featuredImageFileName) &&
            isEmptyString(this.state.featuredImageCaption)) {
            errors.push(["imageCaption", "Image caption is required"]);
        }

        if (isEmptyString(this.state.friendlyUrlName)) {
            errors.push(["friendlyUrl", "Friendly URL name is required"]);
        } else if (!friendlyUrlNameRegExp.test(this.state.friendlyUrlName)) {
            errors.push(["friendlyUrl", "Friendly URL name contains invalid characters"]);
        }

        if (isEmptyString(this.state.summary)) {
            errors.push(["summary", "Summary is required"]);
        }

        if (isEmptyString(this.state.selectedFeed?.feedId)) {
            errors.push(["feed", "Feed is required"]);
        }

        if (isEmptyString(this.state.author)) {
            errors.push(["author", "Author is required"]);
        }

        if (this.state.expiry <= this.state.scheduledDate) {
            errors.push(["expiry", "Expire At must be after Release At date"]);
        }

        return errors;
    }

    private publishSave = async () => {
        this.setState({ isLoading: true });

        try {
            const metadata = this.state.originalMetadata || {
                imageHeight: 0,
                imageName: null,
                imageWidth: 0,
                isCustomFriendlyUrlName: false,
                isCustomSummary: false,
            };
            metadata.isCustomFriendlyUrlName = !this.state.isFriendlyUrlLocked;
            metadata.isCustomSummary = !this.state.isSummaryLocked;

            let featuredImagePath: string = this.state.featuredImageUploadedPath;
            if (_.isEmpty(this.state.featuredImageFileName) && !this.state.featuredImageFile) {
                featuredImagePath = null;
                metadata.imageName = null;
                metadata.imageHeight = 0;
                metadata.imageWidth = 0;
            } else if (this.state.featuredImageFile) {
                const response = await this.props.dataService.uploadFeaturedImage(this.state.featuredImageFile);
                if (response.status === 200) {
                    const data = (await response.json())?.data;
                    featuredImagePath = data.path;
                    metadata.imageName = this.state.featuredImageFile.file.name;
                    metadata.imageHeight = this.state.featuredImageFile.h;
                    metadata.imageWidth = this.state.featuredImageFile.w;
                }
            }

            const news = {
                author: this.state.author,
                body: this.state.body,
                categories: _.uniq(_.map(
                    this.state.selectedCategories,
                    (cat) => cat.categoryId),
                ),
                deleted: this.state.deleted,
                expiry: DateTime.fromJSDate(this.state.expiry).toUTC().toISO(),
                featuredImage: featuredImagePath,
                featuredImageCaption: this.state.featuredImageCaption,
                feeds: [{
                    feedId: this.state.selectedFeed.feedId,
                    pinToTop: this.state.pinToTop,
                }],
                friendlyUrlName: this.state.friendlyUrlName,
                metadata: JSON.stringify(metadata),
                scheduledDate: DateTime.fromJSDate(this.state.scheduledDate).toUTC().toISO(),
                summary: this.state.summary,
                tags: [...this.state.tags],
                title: this.state.title,
                websites: [this.props.config.currentServerId],
            };

            if (this.isEditingExistingPost()) {
                await this.props.dataService.updateNews(this.props.newsId, news);
            } else {
                await this.props.dataService.saveNews(news);
            }
            this.setState({ hasUnsavedChanges: false }, () => {
                this.props.onReloadNewsPosts();
                this.slideOutAndReturn();
            });
        } catch (ex) {
            const rd = ex.response.data;
            const errors = [];
            for (const cat of Object.keys(rd)) {
                for (const msg of rd[cat]) {
                    errors.push([cat?.toLowerCase(), msg]);
                }
            }
            this.setState({
                errors,
                isLoading: false,
            });
        }
    }

    private cancelClick = () => {
        if (this.state.hasUnsavedChanges) {
            this.setState({ isPromptUnsavedOpen: true });
        } else {
            this.slideOutAndReturn();
        }
    }

    private slideOutAndReturn = () => {
        this.setState({ isLoading: true });
        window.removeEventListener("beforeunload", this.onUnload);
        window.removeEventListener("resize", this.onResize);

        setTimeout(() => {
            this.props.onBack();
        }, 800);
    }

    private onUnload = (e: BeforeUnloadEvent) => {
        if (this.state.hasUnsavedChanges === false) {
            return;
        }
        (e || window.event).returnValue = UnsavedConfirmationMessage; // Gecko + IE
        return UnsavedConfirmationMessage; // Gecko + Webkit, Safari, Chrome, etc
    }

    private onResize = (e: UIEvent) => {
        const wasMobileView = this.state.isMobileView;
        const isMobileView = window.innerWidth < 800;
        this.setState({ isMobileView }, () => {
            if (!wasMobileView && isMobileView) {
                this.setState({ isSidebarOpen: false });
            } else if (wasMobileView && !isMobileView) {
                this.setState({ isSidebarOpen: true });
            }
        });
    }

    private convertToJsDate = (date: string) => {
        try {
            let dt = DateTime.fromISO(date, { zone: "utc" }).toLocal();
            // Set the date to max allowed by Kendo DatePicker
            if (dt.year > DatePickerMaxYear) {
                dt = dt.set({ year: DatePickerMaxYear });
            }
            return dt.toJSDate();
        } catch (e) {
            return null;
        }
    }
}

export default Edit;
